import { IconExclamationCircleFilled } from '@u21/tabler-icons';
import { ConsortiumAlertV2EntityResponse } from 'app/modules/consortium/responsesV2';
import { getHighestSeverityColor } from 'app/modules/consortium/utils';
import { ConsortiumAlertsV2Table } from 'app/modules/consortium/components/ConsortiumAlertsV2Table';
import {
  U21Button,
  U21MenuLayout,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import pluralize from 'pluralize';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

interface Props {
  entityData: ConsortiumAlertV2EntityResponse;
  entityExternalId: string;
}

export const ConsortiumV2SummaryPopover = ({
  entityData,
  entityExternalId,
}: Props) => {
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();

  const outcomes = useMemo(
    () =>
      entityData.alerts
        .map((a) => a.investigation_outcome)
        .filter((o): o is NonNullable<typeof o> => o !== null),
    [entityData.alerts],
  );

  if (outcomes.length === 0) {
    return null;
  }

  const color = getHighestSeverityColor(outcomes);

  return (
    <U21MenuLayout
      trigger={
        <U21Button
          icon={<IconExclamationCircleFilled />}
          color={color}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          tooltip="Consortium Alerts"
          aria-label="Consortium Alerts"
        />
      }
      onClose={() => setOpen(false)}
      open={open}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <StyledWrapper>
        <U21Typography variant="subtitle1">
          {pluralize('Consortium Alert', outcomes.length, true)}
        </U21Typography>
        <U21Typography variant="body2" color="text.secondary">
          Visit the{' '}
          <U21Button
            variant="text"
            color="primary"
            size="small"
            onClick={() => setOpen(false)}
            to={{
              pathname: (pathname.startsWith('/alerts')
                ? ROUTES_MAP.alertsIdTab.path.replace(
                    ':id',
                    pathname.split('/')[2],
                  )
                : ROUTES_MAP.entitiesIdTab.path.replace(':id', entityExternalId)
              ).replace(':tab?', 'consortium-hits'),
              search,
            }}
          >
            Consortium Hits
          </U21Button>{' '}
          tab for more details
        </U21Typography>
        <ConsortiumAlertsV2Table
          alerts={entityData.alerts}
          hasMore={entityData.has_more}
          loading={false}
        />
      </StyledWrapper>
    </U21MenuLayout>
  );
};

const StyledWrapper = styled(U21Spacer)`
  padding: 16px;
  min-width: 450px;
  max-width: 700px;
`;
